import { useQuery } from "@tanstack/react-query";
import { hasPermission, hasAnyPermission, hasAllPermissions } from "@shared/permissions";

interface PermissionsResponse {
  permissions: string[];
}

// Module-level empty array keeps the `permissions` reference stable across
// renders when data hasn't loaded yet. Without this, `permissions` would be
// a new `[]` each render and break useMemo/useEffect dependency checks.
const EMPTY_PERMISSIONS: string[] = [];

/**
 * Hook to fetch and check user permissions
 *
 * @example
 * const { can, canAny, permissions, isLoading } = usePermissions();
 *
 * // Check single permission
 * if (can('clients:edit')) { ... }
 *
 * // Check multiple (any match)
 * if (canAny(['clients:edit', 'clients:delete'])) { ... }
 */
export function usePermissions() {
  const { data, isLoading, error } = useQuery<PermissionsResponse>({
    queryKey: ["/api/auth/permissions"],
    queryFn: async () => {
      const response = await fetch("/api/auth/permissions", {
        credentials: "include"
      });

      if (!response.ok) {
        if (response.status === 401) {
          return { permissions: [] };
        }
        throw new Error("Failed to fetch permissions");
      }

      return response.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const permissions = data?.permissions ?? EMPTY_PERMISSIONS;

  return {
    permissions,
    isLoading,
    error,

    /**
     * Check if user has a specific permission
     */
    can: (permission: string): boolean => {
      return hasPermission(permissions, permission);
    },

    /**
     * Check if user has any of the specified permissions
     */
    canAny: (requiredPermissions: string[]): boolean => {
      return hasAnyPermission(permissions, requiredPermissions);
    },

    /**
     * Check if user has all of the specified permissions
     */
    canAll: (requiredPermissions: string[]): boolean => {
      return hasAllPermissions(permissions, requiredPermissions);
    },
  };
}

/**
 * Simple hook that returns just a boolean for a single permission check
 *
 * @example
 * const canEditClients = usePermission('clients:edit');
 */
export function usePermission(permission: string): boolean {
  const { can } = usePermissions();
  return can(permission);
}
